import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { ScoreGauge } from "@/components/ScoreGauge";
import { INTELLIGENCE } from "@/constants/testIds";
import { Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function CareerIntelligence() {
  const { t, lang } = useI18n();
  const [score, setScore] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [recos, setRecos] = useState([]);
  const [busy, setBusy] = useState({ narrative: false, recos: false });

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/intelligence/score"),
          api.get("/intelligence/recommendations"),
        ]);
        setScore(s.data);
        setRecos(r.data.items || []);
      } catch {}
    })();
  }, []);

  const genNarrative = async () => {
    setBusy((b) => ({ ...b, narrative: true }));
    try {
      const { data } = await api.post("/intelligence/score/narrative");
      setScore(data);
      setNarrative(data.narrative);
    } catch { toast.error("AI error"); }
    finally { setBusy((b) => ({ ...b, narrative: false })); }
  };

  const genRecos = async () => {
    setBusy((b) => ({ ...b, recos: true }));
    try {
      const { data } = await api.post("/intelligence/recommendations");
      setRecos(data.items || []);
      toast.success(lang === "fr" ? "Recommandations générées" : "Recommendations ready");
    } catch { toast.error("AI error"); }
    finally { setBusy((b) => ({ ...b, recos: false })); }
  };

  return (
    <div data-testid={INTELLIGENCE.root}>
      <TopBar title={t("intelligence.title")} subtitle={t("intelligence.subtitle")} />
      <div className="px-6 md:px-10 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 md:gap-8">

        {/* Score column */}
        <div className="space-y-4">
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6 flex flex-col items-center">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">Score ALTIORA</div>
            <div data-testid={INTELLIGENCE.scoreValue}>
              <ScoreGauge score={score?.score || 0} band={score?.band || "—"} />
            </div>
            <div data-testid={INTELLIGENCE.scoreBand} className="mt-4 font-mono text-xs uppercase tracking-widest text-[#5B5B62]">
              {lang === "fr" ? "Bande" : "Band"}: <span className="text-[#0B2545]">{score?.band || "—"}</span>
            </div>
            <button
              data-testid={INTELLIGENCE.narrativeBtn}
              onClick={genNarrative}
              disabled={busy.narrative}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#0B2545]/40 hover:border-[#0B2545] text-[#0B2545] text-sm py-2 transition-colors disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {busy.narrative ? t("common.loading") : t("intelligence.narrative")}
            </button>
            {narrative && (
              <div className="mt-4 text-xs text-[#5B5B62] leading-relaxed">{narrative}</div>
            )}
          </div>

          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
              {t("intelligence.dimensions")}
            </div>
            <div className="space-y-3">
              {(score?.dimensions || []).map((d) => (
                <div key={d.key} data-testid={INTELLIGENCE.dimensionRow}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-[#1F2937]">{d[`label_${lang}`]}</span>
                    <span className="font-mono text-[#0B2545] tabular-nums">{d.value}</span>
                  </div>
                  <div className="mt-1 h-1 bg-[#EBE8DE] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0B2545]" style={{ width: `${d.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl tracking-tight text-[#1F2937]">
              {lang === "fr" ? "Métiers recommandés" : "Recommended roles"}
            </h2>
            <button
              data-testid={INTELLIGENCE.recosBtn}
              onClick={genRecos} disabled={busy.recos}
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
            >
              <TrendingUp className="h-4 w-4" strokeWidth={2} />
              {busy.recos ? t("common.loading") : t("intelligence.get_recos")}
            </button>
          </div>
          {recos.length === 0 && (
            <div className="rounded-md border border-dashed border-[#E4E1D8] p-10 text-center text-sm text-[#5B5B62]">
              {lang === "fr" ? "Lancez le moteur de recommandations IA pour explorer vos meilleurs métiers." : "Run the AI recommender to explore your best-fit roles."}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recos.map((r) => (
              <div key={r.id || r.role} data-testid={INTELLIGENCE.recoCard}
                   className="rounded-md border border-[#E4E1D8] bg-white p-5 hover:border-[#B8B5AC] transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-base font-medium text-[#1F2937]">{r.role}</div>
                  <div className="font-mono text-xs text-[#0B2545] tabular-nums whitespace-nowrap">
                    {r.fit_score}/100
                  </div>
                </div>
                <p className="mt-2 text-xs text-[#5B5B62] leading-relaxed">{r.rationale}</p>
                {r.key_skills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.key_skills.slice(0, 5).map((s) => (
                      <span key={s} className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62] border border-[#E4E1D8] rounded px-2 py-0.5">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                {(r.growth_outlook || r.typical_salary) && (
                  <div className="mt-4 pt-3 border-t border-[#E4E1D8] font-mono text-[10px] text-[#5B5B62] flex justify-between">
                    <span>{r.growth_outlook || ""}</span>
                    <span className="text-[#0B2545]">{r.typical_salary || ""}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
